import React from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../contexts/AuthContext";
import CrowdFan from "./CrowdFan";

const formatMeta = (event) => {
  if (!event) return "";
  const base = event.start || event.date;
  let dateStr = "";
  let timeStr = "";
  try {
    if (base) {
      dateStr = new Date(base).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }
    if (event.start) {
      timeStr = new Date(event.start).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    }
  } catch (e) {
    /* ignore formatting issues */
  }
  const venue = [event.venueName, event.venueCity].filter(Boolean).join(", ");
  return [dateStr, timeStr, venue].filter(Boolean).join(" · ");
};

const OrgRow = ({ logo, orgName }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {logo && (
          <Box
            component="img"
            src={logo}
            alt=""
            sx={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", display: "block" }}
          />
        )}
        <Typography sx={{ font: "700 12px Bitter, serif", letterSpacing: ".1em", textTransform: "uppercase", color: "#80756F" }}>
          {orgName}
        </Typography>
      </Box>
      <Typography sx={{ font: "600 10.5px Bitter, serif", letterSpacing: ".12em", textTransform: "uppercase", color: "#B7ACA5" }}>
        {t("walkup.org.tag")}
      </Typography>
    </Box>
  );
};

const EventCard = ({ event, logo }) => (
  <Box
    sx={{
      margin: "18px 22px 0",
      border: "1px solid #EFE6E0",
      borderRadius: "18px",
      padding: "13px 15px",
      display: "flex",
      alignItems: "center",
      gap: "13px",
      background: "#FBF6F2",
    }}
  >
    {logo && (
      <Box component="img" src={logo} alt="" sx={{ width: 42, height: 42, borderRadius: "11px", objectFit: "cover", display: "block", flex: "none" }} />
    )}
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ font: "700 16px Bitter, serif", color: "#211B19" }}>{event?.name}</Typography>
      <Typography sx={{ font: "500 12.5px Bitter, serif", color: "#80756F", mt: "3px" }}>
        {formatMeta(event)}
      </Typography>
    </Box>
  </Box>
);

const Kicker = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        font: "700 11px Bitter, serif",
        letterSpacing: ".14em",
        textTransform: "uppercase",
        color: theme.palette.primary.main,
        background: alpha(theme.palette.primary.main, 0.12),
        borderRadius: "999px",
        padding: "5px 12px",
      }}
    >
      {children}
    </Box>
  );
};

const Stepper3 = ({ activeStep = 0 }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const steps = [t("walkup.step.account"), t("walkup.step.profile"), t("walkup.step.checkin")];
  const fillPct = (activeStep / (steps.length - 1)) * 100;
  return (
    <Box sx={{ position: "relative", margin: "18px 30px 0" }}>
      {/* track + progress fill up to the active node */}
      <Box sx={{ position: "absolute", top: 17, left: 42, right: 42, height: 2, background: "#F0E2DC" }} />
      <Box
        sx={{
          position: "absolute",
          top: 17,
          left: 42,
          width: `calc((100% - 84px) * ${fillPct / 100})`,
          height: 2,
          background: theme.palette.primary.main,
          transition: "width .3s ease",
        }}
      />
      <Box sx={{ position: "relative", display: "flex", justifyContent: "space-between" }}>
        {steps.map((label, i) => {
          const isActive = i === activeStep;
          const filled = i <= activeStep;
          return (
            <Box key={i} sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 84 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  font: "700 15px Bitter, serif",
                  transition: "all .25s ease",
                  ...(filled
                    ? {
                        background: theme.palette.primary.main,
                        color: "#fff",
                        boxShadow: isActive ? `0 6px 14px ${alpha(theme.palette.primary.main, 0.3)}` : "none",
                      }
                    : {
                        background: "#fff",
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        color: alpha(theme.palette.primary.main, 0.7),
                      }),
                }}
              >
                {i + 1}
              </Box>
              <Typography
                sx={{
                  font: `${isActive ? 700 : 600} 11.5px Bitter, serif`,
                  color: isActive ? "#211B19" : "#80756F",
                  mt: "9px",
                  textAlign: "center",
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const STEP_ICONS = [PersonOutlineIcon, CameraAltOutlinedIcon, CheckRoundedIcon];

const StepEyebrow = ({ num, name }) => {
  const { t } = useTranslation();
  return (
    <Typography
      sx={{
        textAlign: "center",
        font: "700 11px Bitter, serif",
        letterSpacing: ".14em",
        textTransform: "uppercase",
        color: "#A1968F",
        mb: 1.5,
      }}
    >
      {t("walkup.help.label", { num, name })}
    </Typography>
  );
};

const HelperCard = ({ step, count }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const Icon = STEP_ICONS[step];
  const titleKey = ["walkup.help.account.title", "walkup.help.profile.title", "walkup.help.checkin.title"][step];
  let body;
  if (step === 2) {
    body = count ? t("walkup.help.checkin.body", { count }) : t("walkup.help.checkin.body_generic");
  } else {
    body = t(["walkup.help.account.body", "walkup.help.profile.body"][step]);
  }
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        background: "#FBF6F2",
        border: "1px solid #EFE6E0",
        borderRadius: "16px",
        padding: "14px 16px",
      }}
    >
      <Box
        sx={{
          flex: "none",
          width: 38,
          height: 38,
          borderRadius: "11px",
          background: alpha(theme.palette.primary.main, 0.12),
          display: "grid",
          placeItems: "center",
        }}
      >
        <Icon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ font: "700 16px/1.25 Bitter, serif", color: "#211B19" }}>
          {t(titleKey)}
        </Typography>
        <Typography sx={{ font: "500 13.5px/1.5 Bitter, serif", color: "#6E635E", mt: "4px" }}>
          {body}
        </Typography>
      </Box>
    </Box>
  );
};

const Seal = () => {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: "50%",
          background: alpha(theme.palette.primary.main, 0.12),
          display: "grid",
          placeItems: "center",
        }}
      >
        <Box
          sx={{
            width: 62,
            height: 62,
            borderRadius: "50%",
            background: theme.palette.primary.main,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CheckRoundedIcon sx={{ color: "#fff", fontSize: 36 }} />
        </Box>
      </Box>
    </Box>
  );
};

const Cta = ({ label, onClick, busy, withArrow }) => {
  const theme = useTheme();
  return (
    <Button
      fullWidth
      variant="contained"
      size="large"
      disabled={busy}
      onClick={onClick}
      endIcon={withArrow && !busy ? <ArrowForwardIcon /> : null}
      sx={{
        borderRadius: "16px",
        padding: "14px 20px",
        font: "600 17px Bitter, serif",
        textTransform: "none",
        boxShadow: `0 12px 26px ${alpha(theme.palette.primary.main, 0.34)}`,
      }}
    >
      {busy ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : label}
    </Button>
  );
};

/**
 * variant: "new" | "returning" | "already"
 * onPrimary: CTA handler. For "new" this dismisses the welcome to reveal the
 * auth form; for "returning"/"already" it registers and routes to check-in.
 */
const WalkUpWelcome = ({ variant = "new", event, count = null, onPrimary, busy = false }) => {
  const { t } = useTranslation();
  const { appStyle, appConfig } = React.useContext(AuthContext);
  const logo = appStyle?.logo;
  const orgName = appConfig?.orgName || "";

  const isNew = variant === "new";
  const isReturning = variant === "returning";

  // For the new-user variant the CTA walks through the 3 steps (priming the
  // user) before dismissing the welcome to reveal the auth form.
  const [activeStep, setActiveStep] = React.useState(0);
  const lastStep = activeStep >= 2;
  const handleNewCta = () => {
    if (lastStep) {
      onPrimary();
    } else {
      setActiveStep((s) => s + 1);
    }
  };
  const stepLabels = [t("walkup.step.account"), t("walkup.step.profile"), t("walkup.step.checkin")];

  let kicker;
  let title;
  let lead;
  let ctaLabel;
  if (isNew) {
    kicker = t("walkup.welcome.kicker");
    title = t("walkup.welcome.title");
    lead = t("walkup.welcome.lead");
    ctaLabel = t("walkup.cta.letsgo");
  } else if (isReturning) {
    kicker = t("walkup.back.kicker");
    title = t("walkup.back.title");
    lead = t("walkup.back.lead");
    ctaLabel = t("walkup.back.cta");
  } else {
    kicker = t("walkup.already.kicker");
    title = t("walkup.already.title");
    lead = t("walkup.already.body");
    ctaLabel = t("walkup.already.cta");
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        maxWidth: 440,
        mx: "auto",
        width: "100%",
        pt: 2,
      }}
    >
      <OrgRow logo={logo} orgName={orgName} />

      {isNew && <CrowdFan count={count} variant="fan" />}
      {isReturning && <CrowdFan count={count} variant="group" />}
      {variant === "already" && <Seal />}

      <Box sx={{ px: "26px", pt: "20px", textAlign: "center" }}>
        <Kicker>{kicker}</Kicker>
        <Typography
          component="h1"
          sx={{ font: "800 30px/1.13 Bitter, serif", color: "#211B19", mt: "14px", letterSpacing: "-.015em", textWrap: "balance" }}
        >
          {title}
        </Typography>
      </Box>

      <EventCard event={event} logo={logo} />

      {!isNew && (
        <Typography
          sx={{ textAlign: "center", font: "500 14.5px/1.5 Bitter, serif", color: "#6E635E", padding: "16px 30px 0" }}
        >
          {lead}
        </Typography>
      )}

      {/* Stepper sits directly under the event card; the helper card floats
          centered in the space between the stepper and the pinned button. */}
      {isNew && (
        <Box sx={{ padding: "22px 22px 0" }}>
          <StepEyebrow num={activeStep + 1} name={stepLabels[activeStep]} />
          <Stepper3 activeStep={activeStep} />
        </Box>
      )}

      {isNew && (
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center", px: "22px", py: 2 }}>
          <HelperCard step={activeStep} count={count} />
        </Box>
      )}

      <Box sx={{ mt: "auto", padding: "16px 22px 26px" }}>
        <Cta
          label={isNew ? (lastStep ? t("walkup.cta.letsgo") : t("walkup.cta.next")) : ctaLabel}
          onClick={isNew ? handleNewCta : onPrimary}
          busy={busy}
          withArrow={isNew}
        />
        <Typography sx={{ textAlign: "center", font: "500 12.5px Bitter, serif", color: "#A1968F", mt: "13px" }}>
          {t("walkup.reassurance")}
        </Typography>
      </Box>
    </Box>
  );
};

export default WalkUpWelcome;
